<template>
  <ion-page>
    <div v-if="sessionRestoring" class="login-boot-bridge" aria-busy="true" aria-live="polite">
      <img :src="logoSrc" alt="Liga" class="login-boot-logo" />
      <ion-spinner name="crescent" class="login-boot-spinner"></ion-spinner>
      <p class="login-boot-text">Загрузка…</p>
    </div>
    <ion-content
      v-show="!authenticated && !sessionRestoring"
      :class="'main-background'"
      :fullscreen="true"
      slot="fixed"
      :scroll-y="true"
    >
      <div class="logo" v-show="showLogo">
        <img :src="logoSrc" alt="liga-logo" />
      </div>
      <div class="login-card-wrapper">
        <ion-card :class="['bg-transparent', showLogo ? '' : 'half-screen-top']">
          <ion-card-header class="ion-text-center white-bold">АВТОРИЗАЦИЯ</ion-card-header>
          <ion-card-content>
            <ion-input
              class="login-field"
              label="Логин (email)"
              label-placement="floating"
              fill="solid"
              type="text"
              v-model="loginData.username"
              :disabled="inlineLoading"
              @ionFocus="onInputFocus()"
              autocomplete="off"
              autocorrect="off"
              @keyup.enter="singIn"
            ></ion-input>

            <ion-input
              class="login-field"
              label="Пароль"
              label-placement="floating"
              fill="solid"
              type="password"
              v-model="loginData.password"
              :disabled="inlineLoading"
              @ionFocus="onInputFocus()"
              autocomplete="off"
              autocorrect="off"
              @keyup.enter="singIn"
            ></ion-input>

            <div class="main-button-container">
              <ion-button @click="singIn()" :disabled="inlineLoading" type="submit">
                <p v-show="!inlineLoading">Вход</p>
                <ion-spinner name="bubbles" v-show="inlineLoading"></ion-spinner>
              </ion-button>
            </div>
            <div class="signup-container">
              <div @click="navigate('signup')">Регистрация</div>
              <div @click="navigate('reset-password')">Забыли пароль?</div>
            </div>
            <div class="not-authorized" @click="navigate('showcase')">Пропустить</div>
          </ion-card-content>
        </ion-card>
      </div>
      <div class="login-legal">
        <div class="login-legal-links">
          <a :href="LEGAL.offerUrl" target="_blank" rel="noopener noreferrer" class="login-legal-link">Договор оферты</a>
          <span @click="navigate('privacy-policy')" class="login-legal-link">Политика конфиденциальности</span>
          <span @click="navigate('offer-supplement')" class="login-legal-link">Условия возврата</span>
        </div>
        <div class="login-legal-payee">
          <p class="login-legal-payee-title">Платежи принимает:</p>
          <p class="login-legal-payee-name">{{ LEGAL.payee.fullName }}</p>
          <p>ИНН {{ LEGAL.payee.inn }} · ОГРНИП {{ LEGAL.payee.ogrnip }}</p>
          <p>{{ LEGAL.payee.address }}</p>
          <p>Тел.: {{ LEGAL.payee.phone }} · {{ LEGAL.payee.email }}</p>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import router from "@/router/index";
import AccountService from "@/services/account.service";
import { hideNativeSplash } from "@/services/native-splash.service";
import { flushPendingPushOpen, registerAdminPushNotifications } from "@/services/push.service";
import store from "@/store/index";
import { Browser } from "@capacitor/browser";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { stripAuthWhitespace } from "@/utils/auth-input";
import { getClientDeviceMeta } from "@/utils/client-device-meta";
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonContent,
    IonInput,
    IonPage,
    IonSpinner,
    alertController,
    toastController,
} from "@ionic/vue";
import { computed, defineComponent, inject } from "vue";
import { LEGAL } from "@/config/legal";
import logoBlack from "@/theme/liga-logo-black.png";
import logoWhite from "@/theme/liga-logo-white.png";

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: "Login",
  components: {
    IonContent,
    IonPage,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonInput,
    IonButton,
    IonSpinner,
  },
  setup() {
    const accountService = inject("accountService") as AccountService;
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const logoSrc = computed(() =>
      (globalContext.effectiveTheme || "dark") === "light" ? logoBlack : logoWhite
    );

    return {
      accountService,
      isMobile,
      globalContext,
      LEGAL,
      logoSrc,
    };
  },
  data() {
    return {
      loginData: {
        username: "",
        password: "",
      },
      login: null,
      password: null,
      rememberMe: "",
      showLogo: true,
      inlineLoading: false,
      /** Restore JWT на native: bridge вместо чёрного экрана после splash. */
      sessionRestoring: Capacitor.isNativePlatform(),
    };
  },
  computed: {
    account() {
      return store.getters.account;
    },
    authenticated() {
      return store.getters.authenticated;
    },
  },
  created() {
    this.inlineLoading = true;
    this.accountService
      .hasAnyAuthorityAndCheckAuth(process.env.VUE_APP_INSTRUCTOR_ROLE)
      .then((response) => {
        this.inlineLoading = false;
        if (response) {
          this.successLogin();
        } else {
          Preferences.remove({ key: 'jwt' });
          store.commit('logout');
          this.finishSessionRestore();
          return;
        }
      })
      .catch(() => {
        this.inlineLoading = false;
        this.finishSessionRestore();
      });
  },
  ionViewWillEnter() {
    this.showLogo = true;
    if (this.globalContext.offlineMode) {
      this.finishSessionRestore();
      router.replace('/showcase');
      return;
    }
    const sessionExpired = this.$route.query.sessionExpired === '1';
    if (sessionExpired) {
      router.replace({ path: '/login', query: {} });
      toastController.create({
        message: 'Сессия истекла. Войдите снова.',
        duration: 4000,
        color: 'warning',
      }).then(t => t.present());
    }
  },
  methods: {
    finishSessionRestore() {
      this.sessionRestoring = false;
      void hideNativeSplash();
    },

    navigate(routeName: string): void {
      void hideNativeSplash();
      router.push(`/${routeName}`);
    },

    onInputFocus() {
      if (this.isMobile) this.showLogo = false;
    },

    onInputBlur() {
      if (this.isMobile) this.showLogo = true;
    },

    async singIn() {
      this.showLogo = true;
      this.inlineLoading = true;

      const username = stripAuthWhitespace(this.loginData.username).toLowerCase();
      const password = stripAuthWhitespace(this.loginData.password);
      this.loginData.username = username;
      this.loginData.password = password;

      const meta = await getClientDeviceMeta();
      const data = {
        username,
        password,
        rememberMe: true,
        deviceName: meta.deviceName || undefined,
        platform: meta.platform || undefined,
        appVersion: meta.appVersion || undefined,
        appBuild: meta.appBuild || undefined,
      };

      const options = {
        url: `${process.env.VUE_APP_API_URL}api/authenticate`,
        data: data,
        headers: { "Content-Type": "application/json" },
      };

      try {
        const response = await CapacitorHttp.post(options);
        if (response.status !== 200 && response.status !== 1001) {
          this.showAlert("Ошибка входа", "Проверьте логин и пароль.");
        }

        const bearerToken =
          response.headers.Authorization || response.headers.authorization || response.headers["Authorization"];
        if (bearerToken && bearerToken.slice(0, 7) === "Bearer ") {
          const jwt = bearerToken.slice(7, bearerToken.length);

          if (jwt && jwt.length > 0) {
            // JWT должен лежать в Preferences до retrieveAccount → upload push-токена.
            await Preferences.set({
              key: "jwt",
              value: jwt,
            });

            // axios (админка и др.) читает токен из localStorage — на телефоне он обязателен так же, как Preferences
            localStorage.setItem("jhi-authenticationToken", jwt);
            sessionStorage.removeItem("jhi-authenticationToken");

            this.accountService.retrieveProfiles();
            await this.accountService.retrieveAccount();
            this.successLogin();
          } else {
            throw new Error("Bad jwt");
          }
        } else {
          throw new Error("Bad jwt");
        }
      } catch (err: any) {
        if (err?.message === "Network Error" || err?.status === 408) {
          this.$router.replace("/badconnection");
        } else {
          this.showAlert("Ошибка входа", "Проверьте логин и пароль.");
        }
      } finally {
        this.inlineLoading = false;
      }
    },

    successLogin() {
      this.inlineLoading = false;
      this.finishSessionRestore();
      // На native гарантируем upload FCM/HMS токена даже если retrieveAccount уже отработал.
      void registerAdminPushNotifications().then(() => flushPendingPushOpen());

      this.$nextTick(function () {
        if (!this.globalContext.offlineMode) {
          this.accountService
            .hasAnyAuthorityAndCheckAuth("ROLE_MANAGER")
            .then((value) => {
              this.globalContext.isManager = value;
            });
        } else {
          this.globalContext.isManager = false;
        }

        const redirect = this.parseSafeRedirectPath(this.$route.query.redirect);
        const fromShowcase = this.$route.query.fromShowcase === '1';
        if (redirect) {
          if (fromShowcase) {
            void router.replace({ name: 'Showcase' }).then(() => {
              void router.push(redirect);
            });
          } else {
            void router.replace(redirect);
          }
        } else {
          void router.replace({ name: 'Showcase' });
        }
      });
    },

    /** Только внутренние пути (после входа с витрины и т.п.). */
    parseSafeRedirectPath(raw: unknown): string | null {
      if (raw == null || typeof raw !== 'string') return null;
      let p = raw.trim();
      try {
        p = decodeURIComponent(p).trim();
      } catch {
        return null;
      }
      if (!p.startsWith('/') || p.startsWith('//')) return null;
      if (/^\/events\/\d+$/.test(p)) return p;
      if (/^\/subscriptions\/\d+$/.test(p)) return p;
      return null;
    },

    async register() {
      await Browser.open({ url: `${process.env.VUE_APP_REGISTER_URL}` }).catch(
        (err) => {
          console.log(err);
        }
      );
    },

    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController.create({
        header: alertTitle,
        message: alertMessage,
        buttons: ["OK"],
      });

      await alert.present();
    },
  },
});
</script>

<style scoped>
.login-boot-bridge {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: calc(24px + env(safe-area-inset-top, 0px)) 24px calc(24px + env(safe-area-inset-bottom, 0px));
  background: var(--liga-page-bg, #121212);
  color: var(--liga-chrome-text);
}
.login-boot-logo {
  width: min(220px, 58vw);
  height: auto;
}
.login-boot-spinner {
  width: 36px;
  height: 36px;
  color: var(--ion-color-orange);
}
.login-boot-text {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--liga-chrome-text-muted);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.logo {
  margin: 10vh 0 -6vh 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.logo img {
  width: 300px;
  max-width: 72vw;
  height: auto;
}

.half-screen-top {
  margin-top: 50%;
}

ion-card-header {
  letter-spacing: 0.08em;
  font-size: 2em;
  color: var(--liga-chrome-text);
}

.login-field {
  --background: var(--liga-float-glass-bg-strong);
  --border-radius: 25px;
  --border-width: 1px;
  /* Глобально в theme ion-input сбрасывает --border-color в transparent. */
  --border-color: var(--liga-float-glass-border) !important;
  --border-style: solid;
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
  --highlight-color: transparent;
  --highlight-color-focused: transparent;
  --highlight-height: 0px;
  --padding-start: 1.25em;
  --padding-end: 1.25em;
  margin-bottom: 1.25em;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  overflow: hidden;
  border-radius: 25px;
}

.login-field.ion-focused,
.login-field.has-focus {
  --border-color: rgba(235, 89, 82, 0.55) !important;
}

.login-field::part(native) {
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent;
}

ion-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --color: #ffffff;
  width: 280px;
  height: 50px;
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-weight: bold;
  --border-radius: 14px;
}

.main-button-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.signup-container {
  margin-top: 2vh;
  display: flex;
  flex: row nowrap;
  justify-content: space-between;
  color: var(--liga-chrome-text);
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;
}

.not-authorized {
  color: var(--liga-chrome-text-muted);
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;  
}

.login-card-wrapper {
  z-index: 10;
}

.login-legal {
  padding: 2vh 16px 4vh;
  color: var(--liga-chrome-text-muted);
  font-size: 0.9rem;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}
.login-legal-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5em 1em;
  margin-bottom: 1.5em;
}
.login-legal-link {
  color: var(--ion-color-orange);
  cursor: pointer;
  text-decoration: none;
}
.login-legal-link:hover {
  text-decoration: underline;
}
.login-legal-payee {
  text-align: center;
  font-size: 0.85em;
  line-height: 1.5;
  opacity: 0.95;
}
.login-legal-payee-title {
  font-weight: 600;
  margin-bottom: 0.25em;
}
.login-legal-payee-name {
  font-weight: 600;
  margin-bottom: 0.35em;
}
.login-legal-payee p {
  margin: 0.2em 0;
}

/* 
  ##Device = Desktops
  ##Screen = 1281px to higher resolution desktops
*/

@media (min-width: 1281px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Laptops, Desktops
  ##Screen = B/w 1025px to 1280px
*/

@media (min-width: 1025px) and (max-width: 1280px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Tablets, Ipads (portrait)
  ##Screen = B/w 768px to 1024px
*/

@media (min-width: 768px) and (max-width: 1024px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Tablets, Ipads (landscape)
  ##Screen = B/w 768px to 1024px
*/
@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}
</style>